import { DROPDOWN_APPS } from './AppNavbar.js'
import { checkActiveMember, isIntranetRoute } from '../functions/appHeader.js'

const TITLES = {
  '/calendar': 'Calendar',
  '/chat': 'Chat',
  '/tickets': 'My view',
  '/tickets/my': 'My view',
  '/tickets/general': 'General',
  '/tickets/feed': 'Feed',
  '/tickets/canvas': 'Canvas',
  '/tickets/stories': 'Stories',
  '/tickets/epics': 'Epics',
  '/tickets/board': 'Board',
  '/tickets/standups': 'Standups',
  '/meet': 'Meet',
  '/system': 'System',
  '/dashboard': 'Dashboard'
}

const PREFIX_TITLES = [
  ['/tickets/requests', 'Requests'],
  ['/tickets/resolutions', 'Resolutions'],
  ['/tickets', 'Tickets'],
  ['/meet/', 'Call'],
  ['/documents/', 'Documents'],
  ['/documents', 'Documents'],
  ['/studio', 'Studio'],
  ['/admin', 'Admin']
]

const pageTitle = (path) => {
  if (!path) return ''
  if (TITLES[path]) return TITLES[path]
  if (path.startsWith('/meet/') && path.endsWith('/transcript'))
    return 'Transcript'
  for (const [prefix, label] of PREFIX_TITLES) {
    if (path.startsWith(prefix)) return label
  }
  return 'Canvas'
}

export const AppHeader = {
  extends: 'Navbar',
  theme: null,
  padding: 'X2',
  gap: 'A',
  position: 'sticky',
  top: '0',
  zIndex: '50',
  align: 'center space-between',
  hide: (el, s) => {
    const path = s.root?.currentPath || '/'
    return path === '/login' || path === '/signin'
  },

  Logo: {
    extends: 'Link',
    href: '/',
    hide: (el) => checkActiveMember(el),
    cursor: 'pointer',
    color: 'title',
    padding: 'Y',
    align: 'center center',
    LogoIcon: {
      icon: 'logov2',
      boxSize: 'A A',
      aspectRatio: '1 / 1'
    }
  },

  // NavbarTooltip lives here (not inside AppNavbar) because `children: APPS`
  // in AppNavbar replaces all named children. P is forced to always render
  // so setNavbarTooltipPosition can write into P.node.textContent on hover.
  NavbarTooltip: {
    P: { if: () => true, text: '' }
  },

  AppNavbar: {
    hide: (el) => !checkActiveMember(el),
    fontSize: 'A'
  },

  AppNavbarDropdown: {
    extends: 'Dropdown',
    top: '105%',
    left: 'X2',
    width: 'G',
    minWidth: 'G',
    theme: 'quaternary',

    onInit: (el) => {
      el._closeHandler = (e) => {
        if (!el.node || !el.forcedVisible) return
        if (el.node.contains(e.target)) return
        const navbarNode = el.parent?.AppNavbar?.node
        if (navbarNode && navbarNode.contains(e.target)) return
        el.forcedVisible = false
        el.update({
          style: { opacity: '0', visibility: 'hidden', pointerEvents: 'none' }
        })
      }
      document.addEventListener('mousedown', el._closeHandler)
    },
    onRemove: (el) => {
      if (el._closeHandler)
        document.removeEventListener('mousedown', el._closeHandler)
    },

    childExtends: 'AppNavbarDropdownItem',
    childrenAs: 'state',
    children: DROPDOWN_APPS
  },

  PageTitle: {
    hide: (el, s) => isIntranetRoute(s),
    tag: 'span',
    width: 'G',
    margin: '0',
    fontSize: 'A',
    fontWeight: '600',
    color: 'title',
    text: (el, s) => pageTitle(s.root?.currentPath || s.root?.route || '')
  },

  // Global in-app search. Consumer fills `Form.Input.onInput` with its own
  // search source and may add extra `Dropdown` children for result rows.
  SearchBar: {
    extends: 'SearchBar',
    order: '1',
    flex: '1',
    maxWidth: 'H',
    margin: '- auto',

    Form: {
      Input: {
        width: '100%',
        padding: 'Y2 A Y2 C',
        placeholder: 'Search…',
        value: (el, s) => s.root?.navSearchQuery || '',
        onInput: () => {},
        onKeydown: (ev, el) => {
          if (ev.key === 'Escape') el.node.blur()
        }
      }
    },

    Dropdown: {
      width: '100%',
      left: '0',
      maxHeight: '70vh',
      overflow: 'auto',
      padding: 'Y',
      flow: 'y',

      LoadingRow: {
        if: (el, s) =>
          s.root?._navSearchLoading && !s.root?.navSearchResults?.length,
        padding: 'A',
        textAlign: 'center',
        color: 'caption',
        fontSize: 'Z2',
        text: 'Searching…'
      },

      EmptyRow: {
        if: (el, s) =>
          !s.root?._navSearchLoading &&
          s.root?.navSearchQuery &&
          !s.root?.navSearchResults?.length,
        padding: 'A',
        textAlign: 'center',
        color: 'caption',
        fontSize: 'Z2',
        text: (el, s) => `No results for "${s.root?.navSearchQuery || ''}"`
      }
    }
  },

  // User-facing actions on the right of the header. Slots are pre-listed
  // here so consumers that extend this header get them in a consistent
  // order; intranet/canvas fill in the real content via their own
  // component registrations (AppWorkspaceSwitcher, NotificationBell, etc.
  // are provided by sharedLibraries). Non-member views hide everything
  // except the avatar menu.
  UserActions: {
    order: '2',
    flow: 'x',
    align: 'center right',
    gap: 'A',
    padding: 'Y X',

    AppWorkspaceSwitcher: {
      hide: (el) => !checkActiveMember(el)
    },
    CalOnlineUsersBar: {
      hide: (el) => !checkActiveMember(el),
      maxAvatars: 6
    },
    Messenger: {
      hide: (el) => !checkActiveMember(el)
    },
    NotificationWrapper: {
      hide: (el) => !checkActiveMember(el),
      position: 'relative',
      NotificationBell: {},
      NotificationPanel: {}
    },
    AppAssistantToggle: {
      // Two conditions must both pass for the toggle to show:
      //   1. User is an active member (intranet auth gate)
      //   2. aiStream is registered — canvas has no backend, so the toggle
      //      must stay hidden there. The base AppAssistantToggle already
      //      gates on aiStream; repeat the check here so the membership
      //      override doesn't accidentally re-show it on no-backend apps.
      hide: (el) => {
        if (!checkActiveMember(el)) return true
        const fn = el?.context?.functions?.aiStream
        return typeof fn !== 'function'
      }
    },

    MeWrapper: {
      position: 'relative',
      order: '99',

      Me: {
        extends: 'Avatar',
        avatarType: 'initials',
        boxSize: 'B1',
        cursor: 'pointer',
        src: (el, s) => el.call('avatarUrlFor', s.root?.currentUserEmail),
        ':hover': { filter: 'brightness(1.15)' },
        onClick: (e, el, s) => {
          e.stopPropagation()
          s.root.update({ showMeMenu: !s.root.showMeMenu })
        }
      },

      MeMenu: {
        position: 'absolute',
        top: '115%',
        right: '0',
        zIndex: '200',
        flow: 'y',
        gap: 'V',
        padding: 'Z',
        round: 'Z',
        minWidth: 'G',
        background: 'overlayStrong',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'line',
        boxShadow: '0 8px 24px var(--color-shadow)',
        opacity: (el, s) => (s.root?.showMeMenu ? '1' : '0'),
        pointerEvents: (el, s) => (s.root?.showMeMenu ? 'auto' : 'none'),
        transition: 'opacity 120ms ease',

        onCreate: (el, s) => {
          el._close = (ev) => {
            if (!el.node || !el.node.parentElement) return
            if (el.node.parentElement.contains(ev.target)) return
            s.root.update({ showMeMenu: false })
          }
          const doc = el.context?.document || document
          doc.addEventListener('mousedown', el._close)
        },
        onRemove: (el) => {
          if (el._close) {
            const doc = el.context?.document || document
            doc.removeEventListener('mousedown', el._close)
          }
        },

        childExtends: {
          tag: 'button',
          type: 'button',
          flow: 'x',
          align: 'center left',
          gap: 'Z1',
          padding: 'Z1 A',
          round: 'Y',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          fontSize: 'Z2',
          fontWeight: '500',
          width: '100%',
          textAlign: 'left',
          ':hover': { background: 'overlay' }
        },

        ProfileItem: {
          text: 'Profile',
          color: 'title',
          onClick: (e, el, s) => {
            s.root.update({
              showMeMenu: false,
              profileEmail: s.root?.currentUserEmail
            })
          }
        },

        // Theme cycle button. Per smbls/CLAUDE.md global-theme contract,
        // the source of truth is `el.context.globalTheme` — bootShell
        // pipes our themeMode into that on create() and changeGlobalTheme
        // updates it. Reading from root state would drift from the
        // framework attribute on cold load. Persistence still goes through
        // localStorage so the next page load picks the same mode up.
        ThemeItem: {
          color: 'title',
          text: (el, s) => {
            const mode = el?.context?.globalTheme || s.root?.themeMode || 'auto'
            return `Theme: ${mode}`
          },
          onClick: (e, el, s) => {
            const order = ['auto', 'light', 'dark']
            const current = el?.context?.globalTheme || s.root?.themeMode || 'auto'
            const next = order[(order.indexOf(current) + 1) % order.length]
            // changeGlobalTheme updates context.globalTheme + flips
            // data-theme (or removes it for 'auto'). Mirror to root state
            // so any UI still reading `s.root.themeMode` re-renders, and
            // persist for the next page load.
            try { localStorage.setItem('themeMode', next) } catch (_) { /* private mode */ }
            s.root.update({ themeMode: next })
            el.call('changeGlobalTheme', next)
          }
        },

        MenuDivider: {
          // childExtends above sets `tag: 'button'` for menu items.
          // Override here so the divider is a non-interactive separator
          // rather than an unlabeled focusable button.
          tag: 'div',
          attr: { role: 'separator', 'aria-orientation': 'horizontal' },
          cursor: 'default',
          height: '1px',
          background: 'line',
          padding: '0',
          margin: 'V 0'
        },

        // Consumer overrides `onClick` + `href` with its own sign-out flow.
        SignOutItem: {
          extends: 'Link',
          text: 'Sign out',
          color: 'red',
          href: '#',
          onClick: () => {}
        }
      }
    }
  }
}

export const AppNavLink = {
  extends: 'Link',
  cursor: 'pointer',
  padding: 'X1 A',
  round: 'C',
  textDecoration: 'none',
  transition: 'all 200ms ease',
  color: 'caption',
  fontSize: 'Z2',
  fontWeight: '500',
  href: (el, s) => s.href,
  text: (el, s) => s.label,
  background: (el, s) =>
    s.root.currentPath === s.href ? 'blue.2' : 'transparent',
  '@hover': { background: 'overlay' }
}
